const LINE_ITEM = {
  ID: Math.round(Math.random() * 1000000),
  NAME: "Unnamed Project",
  TYPE: "UNKNOWN",
  OBJECT_ID: null,
  LOGO: null,
  AMOUNT_DISCOUNT: 0,
  AMOUNT_REFUNDED: 0,
  AMOUNT_TAX: 0,
  AMOUNT: 0,
  TOTAL: 0,
  QUANTITY: 1,
  NOTES: null,
  TAX_RATES: [],
  DISCOUNTS: [],
  SELECTIONS: [],
};

const LINE_ITEM_SELECTION = {
  MODIFIER_NAME: null,
  MODIFIER_TYPE: null,
  MODIFIER_ID: null,
  OPTION_NAME: null,
  OPTION_ID: null,
  MODIFIER: 1,
  AMOUNT_TAX: 0,
  AMOUNT_DISCOUNT: 0,
  AMOUNT: 0,
  TOTAL: 0,
  QUANTITY: 1,
  SEQ: 1,

  TAX_RATES: [],
  DISCOUNTS: [],
};

const LINE_ITEM_TAX = {
  TAX_ID: null,
  AMOUNT_ADJUSTED: 0,
  AMOUNT_TAX: 0,
  AMOUNT: 0,
};

const LINE_SELECTION_TAX = {
  TAX_ID: null,
  LINE_ITEM_SELECTION_ID: null,
  AMOUNT_ADJUSTED: null, // null if exclusive
  AMOUNT_TAX: 0, // TAX PCT
  AMOUNT: 0,
};

const TICKET_DISCOUNT = {
  DISCOUNT_ID: null, // one of DISCOUNT_ID and PROMOTION_ID should remain null
  PROMOTION_ID: null,
  IS_TICKET_DISCOUNT: true,
  PERCENT_AMOUNT: null, // null if flat, otherwise the percentage
  AMOUNT_ADJUSTED: 0, // Actual amount that gets applied to the ticket
  AMOUNT: 0,
  DATE_PAYED: null,
};

const LINE_ITEM_DISCOUNT = {
  DISCOUNT_ID: null, // one of DISCOUNT_ID and PROMOTION_ID should remain null
  PROMOTION_ID: null,
  IS_TICKET_DISCOUNT: false,
  PERCENT_AMOUNT: null, // null if flat, otherwise, the percentage
  AMOUNT_ADJUSTED: 0, // actual amount that is applied
  AMOUNT: 0, // True amount of the discount (this may not be the actual if the discount is larger than the cart total)
};

const LINE_SELECTION_DISCOUNT = {
  DISCOUNT_ID: null,
  PROMOTION_ID: null,
  IS_TICKET_DISCOUNT: false,
  LINE_ITEM_SELECTION_ID: null,
  PERCENT_AMOUNT: null, // null if flat, otherwise, the percentage
  AMOUNT_ADJUSTED: 0, // the actual amount applied e.g. $4 if the item is $4 and the discount's amount is $5
  AMOUNT: 0, // the true amount of the discount e.g $5 for a $5 discount (Thinking we may not actually need this since why would you care about the whole discount amount just looking at the selection)
};

const CUSTOMIZATION_TYPES = {
  SINGLE_SELECT: 0,
  QUANTITY: 1,
  MULTI_SELECT: 2,
  MULTI_SELECT_QUANTITY: 3,
  MULTI_SELECT_FIXED: 4,
  VARIANT: 5,
  AUTOMATIC_VARIANT: 6,
};

const DISCOUNT_TYPES = {
  FLAT: 0,
  PERCENT: 1,
};

const TICKET_DISCOUNT_TYPES = {
  FLAT: 0,
  PERCENT: 1,
};

const MULTIPLIER_MODIFIERS = [0, 1, 2, 3, 4];

/**
 * Calculates the cart
 *
 * @param items             Item
 * @param discounts         Ticket discounts
 */
export function recalculateCart(items, discounts) {
  return updateCartItem(items, discounts);
}

export function updateCartItem({
  items,
  discounts = [],
  cartItem = null,
  removeItem = false,
  debug = false,
  settings = {},
}) {
  // NOTES:
  //    We are using the selections as the "source of truth" for all amounts. This means that the Line item amounts are
  //    defined by the sum of its line item selections.
  //
  //    **HOWEVER** Product discounts will be stored in the Line Item discounts array and distributed over each of the
  //    line item selections via a waterfall algorithm
  //
  //    Similarly, Ticket discounts will be distributed via waterfall over each of the line items, then that discount
  //    is interpreted as a pseudo-product discount and is again waterfalled over its line item selections
  //
  //    Unlike product discounts (which are stored in the line item then distributed to the selections), tax rates are
  //    stored in the selections TAX_RATES array then propagated up to the TAX_RATES array in the line item.
  //
  //    WATERFALL ALGORITHM:
  //      Given: A single line item with an array of discounts and an array of selections. Each selection has an empty
  //             array where applied discounts will be stored and each selection has its AMOUNT_DISCOUNT set to 0.
  //
  //      Algorithm:
  //        1. sort the product discounts Flat discounts high to low then percent discounts high to low
  //        2. sort the selections from largest to smallest AMOUNT
  //        3. for each discount
  //            - for each line item selection
  //              if the selection amount is already zero then continue
  //              - FLAT DISCOUNTS:
  //                  - set the discount's AMOUNT_ADJUSTED to 0
  //                  - Apply as much of the flat discount to this selection as possible
  //                  - take the amount that was applied and add it to the discount AMOUNT_ADJUSTED
  //                  - Add a copy the discount to the selection's discount array with AMOUNT_ADJUSTED set to what was
  //                    applied to that selection
  //                  - if there is a remainder continue to the next item and repeat until we are out of items or the
  //                    remaining discount to apply is 0
  //                      - if we run out of items to apply to then we can prune since all the selections will have amount
  //                        zero
  //                      - if the discount hits zero first then we can break and go to the next discount
  //
  //              - PERCENT DISCOUNTS:
  //                  - set the discount's AMOUNT & AMOUNT_ADJUSTED to 0
  //                  - calculate the selection AMOUNT * discount percent. This is the discount amount & amount adjusted
  //                  - copy the discount to the selection's discounts array with the amount & amount adjusted set to
  //                    the amount that was applied to that selection only
  //
  // STEPS:
  //    1. Backfill cart
  //        - subtract taxes out from item
  //        - add all discounts back to item
  //        - remove all ticket discounts from each item
  //
  //    2. Add in any new item or take out any removed item
  //
  //    3. Recalculate Cart Item Totals
  //       - separate the inclusive tax rate items from exclusive tax rate items
  //          - The reason we are doing this is the following:
  //            Going into this step, items with exclusive tax rates are in a state where they have their pre-tax amount
  //            equal to the AMOUNT. However, inclusive tax rated items are in a state where AMOUNT is equal to their
  //            post-tax price, and we subtract the taxes during the tax calculation step. But, for both types of
  //            items we must calculate the discount on the pre-tax amount. So there is another step we must perform on
  //            the inclusive tax rated items which is described below.
  //       - For inclusive tax rated items, first "calculate the taxes" (this actually means calculate the tax amount
  //       and SUBTRACT to get the pre-tax amount which we store in the AMOUNT).
  //
  //       - recalculate product discount amounts for all items (since now all items have AMOUNT as the pre-tax price)
  //       - recalculate ticket discount amounts and add to individual items
  //       - adjust the inclusive taxes for discounted items
  //          this is necessary since the inclusive tax rated items are discounted correctly but the taxes on them were
  //          calculated based on the non-discounted amount. So we calculate the taxes as if they were exclusive tax
  //          rates from the pre-tax (discounted) AMOUNT values so we know how much the new taxes will be
  //       - recalculate taxes on exclusive tax rated items and add them back to the items

  // TODO have to change the way we do custom products to have the price be dependent on the selections
  const {GIFT_CARD_DISCOUNTS = "0"} = settings;
  const discountGiftCards = GIFT_CARD_DISCOUNTS === "1";

  const [products, fees, giftCards, other, paid] = items.reduce(
    (accum, item) => {
      if (item.DATE_PAYED) {
        accum[4].push(item);
      } else if (item.TYPE === "PRODUCT" || item.TYPE === "CUSTOM_PRODUCT") {
        accum[0].push(item);
      } else if (item.TYPE === "FEE" || item.TYPE === "TICKET_FEE") {
        accum[1].push(item);
      } else if (item.TYPE === "GIFT_CARD") {
        accum[2].push(item);
      } else {
        accum[3].push(item);
      }

      return accum;
    },
    [[], [], [], [], []]
  );

  const [paidDiscountsOg, unpaidDiscountsOg] = discounts.reduce(
    (accum, discount) => {
      if (discount.DATE_PAYED) {
        accum[0].push(discount);
      } else {
        accum[1].push(discount);
      }

      return accum;
    },
    [[], []]
  );

  const cartPayload = _deepClone(products);
  const paidDiscounts = _deepClone(paidDiscountsOg);
  const unpaidDiscounts = _deepClone(unpaidDiscountsOg);
  const cartFees = _deepClone(fees);
  const cartGiftCards = _deepClone(giftCards);
  const cartOther = _deepClone(other);

  for (let item of cartPayload) {
    backfillItem(item);
  }

  if (discountGiftCards) {
    for (const giftCard of cartGiftCards) {
      backfillItem(giftCard);
    }
  }

  if (debug) {
    console.log(
      "BACK FILLED >> ",
      _deepClone(cartPayload),
      _deepClone(unpaidDiscounts)
    );
    console.log(cartItem);
  }

  if (cartItem) {
    if (Array.isArray(cartItem)) {
      for (const newItem of cartItem) {
        handleCartItem(
          newItem,
          cartPayload,
          cartFees,
          cartGiftCards,
          cartOther,
          removeItem
        );
      }
    } else {
      handleCartItem(
        cartItem,
        cartPayload,
        cartFees,
        cartGiftCards,
        cartOther,
        removeItem
      );
    }
  }

  if (debug) {
    console.log(
      "ITEM INSERT >> ",
      _deepClone(cartPayload),
      _deepClone(unpaidDiscounts)
    );
  }

  const itemsToDiscount = discountGiftCards
    ? [...cartPayload, ...cartGiftCards]
    : [...cartPayload];

  const [inclusiveProducts, exclusiveProducts] = itemsToDiscount.reduce(
    (accum, _item) => {
      if (hasInclusiveTaxRate(_item)) {
        accum[0].push(_item);
      } else {
        accum[1].push(_item);
      }

      return accum;
    },
    [[], []]
  );

  if (inclusiveProducts.length > 0) {
    calculateTaxes(inclusiveProducts);
  }

  if (debug) {
    console.log(
      "POST INCLUSIVE TAX >> ",
      _deepClone(cartPayload),
      _deepClone(unpaidDiscounts)
    );
  }

  calculateDiscounts(itemsToDiscount, unpaidDiscounts);

  if (debug) {
    console.log(
      "POST DISCOUNT >> ",
      _deepClone(cartPayload),
      _deepClone(unpaidDiscounts)
    );
  }

  recalculateInclusiveTaxes(inclusiveProducts);

  calculateTaxes(exclusiveProducts);

  if (debug) {
    console.log(
      "POST TAXES >> ",
      _deepClone(cartPayload),
      _deepClone(unpaidDiscounts)
    );
  }

  propagateCart(itemsToDiscount);

  if (debug) {
    console.log(
      "LINE ITEMS UPDATED >> ",
      _deepClone(itemsToDiscount),
      _deepClone(unpaidDiscounts)
    );
  }

  // the reason we do this is because gift cards are included in itemsToDiscount in one case
  // And below we can't separate ...cartGiftCards from ...cartPayload because we edit itemsToDiscount in a splice
  // inside of calculateDiscounts so that must be maintained.

  const newGiftCards = discountGiftCards ? [] : [...cartGiftCards];
  const newCart = [
    ...paid,
    ...itemsToDiscount,
    ...cartFees,
    ...newGiftCards,
    ...cartOther,
  ];

  return {
    items: [...newCart],
    discounts: [...paidDiscounts, ...unpaidDiscounts],
  };
}

export function backfillItem(item) {
  backfillTaxes(item);
  backfillDiscounts(item);
  removeTicketDiscounts(item);
}

function backfillTaxes(item) {
  for (let selection of item.SELECTIONS) {
    if (selection.TAX_RATES) {
      for (let tax of selection.TAX_RATES) {
        if (tax.AMOUNT_ADJUSTED !== null && tax.AMOUNT_ADJUSTED !== undefined) {
          selection.AMOUNT += tax.AMOUNT_ADJUSTED;

          tax.AMOUNT_ADJUSTED = 0;
        }
      }
    }

    selection.AMOUNT_TAX = 0;
    selection.TOTAL = selection.AMOUNT;
  }
}

/**
 * Loops over line item selections and adds the discounts applied back to the selection, then sets the discount array to
 * be empty.
 *
 * @param item
 */
function backfillDiscounts(item) {
  for (let select of item.SELECTIONS) {
    select.AMOUNT += select.AMOUNT_DISCOUNT ?? 0;
    select.AMOUNT_DISCOUNT = 0;
    select.DISCOUNTS = [];
    select.TOTAL = select.AMOUNT;
  }
}

/**
 * Removes ticket discounts from a product's discount list
 *
 * @param item
 */
function removeTicketDiscounts(item) {
  item.DISCOUNTS =
    item?.DISCOUNTS?.filter((discount) => !discount.IS_TICKET_DISCOUNT) ?? [];
}

/**
 * Given a line item with its product discounts in the DISCOUNTS array, distribute those discounts over its selections
 *
 * @param item
 */
function calculateProductDiscounts(item) {
  // Sort the line item discount for waterfall
  item.DISCOUNTS.sort((a, b) => {
    if (!a.IS_TICKET_DISCOUNT) {
      if (b.IS_TICKET_DISCOUNT) {
        return -1;
      }

      return discountCmp(a, b);
    } else {
      if (!b.IS_TICKET_DISCOUNT) {
        return 1;
      }

      return discountCmp(a, b);
    }
  });

  item.SELECTIONS.sort((a, b) => {
    return b.AMOUNT - a.AMOUNT;
  });

  let prune = false;

  for (const discount of item.DISCOUNTS) {
    // we don't reset ticket discount amounts since they are referenced in other items as well
    if (!discount.IS_TICKET_DISCOUNT) {
      discount.AMOUNT_ADJUSTED = 0; // this amount gets added to
    }

    if (prune) {
      if (discount.PERCENT_AMOUNT !== null) {
        discount.AMOUNT = discount.AMOUNT_ADJUSTED;
      }
      continue;
    }

    for (const selection of item.SELECTIONS) {
      if (selection.AMOUNT === 0) {
        continue;
      }

      if (
        discount.PERCENT_AMOUNT === null &&
        discount.AMOUNT === discount.AMOUNT_ADJUSTED
      ) {
        // This is a flat discount that has been used up
        // discount.AMOUNT_ADJUSTED =
        //   discount.AMOUNT_ADJUSTED * (item.QUANTITY || 1);

        break;
      }

      applyDiscountToSelection(discount, selection, item.QUANTITY);
    }

    // The whole discount was not used up also it is flat
    if (
      discount.PERCENT_AMOUNT === null &&
      discount.AMOUNT > discount.AMOUNT_ADJUSTED
    ) {
      // we can prune here since no more discounts will be used & all the selections are zero
      prune = true;
    }

    if (discount.PERCENT_AMOUNT !== null) {
      discount.AMOUNT = discount.AMOUNT_ADJUSTED;
    } else {
      discount.AMOUNT_ADJUSTED *= item.QUANTITY;
    }
  }
}

/**
 * Given a list of ticket discounts and a cart (which already has its product discounts distributed and propagated back
 * up to the line item), distribute the ticket discounts over all the items in the cart then distribute those discounts
 * over the selections in those items. It won't propagate these discounts up to the item so we must do that again.
 *
 * @param items
 * @param ticketDiscounts
 */
function calculateTicketDiscounts(items, ticketDiscounts) {
  // Waterfall each flat ticket discount into the items
  // then distribute percent discounts evenly across the remaining items

  ticketDiscounts.sort(discountCmp);

  items.sort((a, b) => {
    return b.AMOUNT - a.AMOUNT;
  });

  let prune = false;

  for (const discount of ticketDiscounts) {
    discount.AMOUNT_ADJUSTED = 0; // this amount gets added to
    discount.IS_TICKET_DISCOUNT = true;

    if (prune) {
      if (discount.PERCENT_AMOUNT !== null) {
        discount.AMOUNT = discount.AMOUNT_ADJUSTED;
      }

      continue;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.AMOUNT === 0) {
        continue;
      }

      if (
        discount.PERCENT_AMOUNT === null &&
        discount.AMOUNT === discount.AMOUNT_ADJUSTED
      ) {
        // Flat discount that has been used all the way up
        break;
      }

      // In this unique case, we have to separate the two (or more) items into
      if (
        item.QUANTITY > 1 &&
        discount.PERCENT_AMOUNT === null &&
        discount.AMOUNT - discount.AMOUNT_ADJUSTED > 0 &&
        discount.AMOUNT - discount.AMOUNT_ADJUSTED < item.AMOUNT
      ) {
        const otherItem = _deepClone(item);
        otherItem.ID = Math.round(Math.random() * 1000000);
        otherItem.QUANTITY -= 1;
        item.QUANTITY = 1;
        items.splice(i + 1, 0, otherItem);
      }

      applyDiscountToItem(discount, item);
    }

    // The whole discount was not used up also it is flat
    if (
      discount.PERCENT_AMOUNT === null &&
      discount.AMOUNT > discount.AMOUNT_ADJUSTED
    ) {
      // we can prune here since no more discounts will be used & all the selections are zero
      prune = true;
    }

    if (discount.PERCENT_AMOUNT !== null) {
      discount.AMOUNT = discount.AMOUNT_ADJUSTED;
    }
  }
}

/**
 * Given a discount and a selection, distribute by updating the contents of the selection as well as the discount
 *
 * @param discount
 * @param selection
 */
function applyDiscountToSelection(discount, selection, itemQuantity = 1) {
  const discountPayload = {
    ...LINE_SELECTION_DISCOUNT,
    IS_TICKET_DISCOUNT: discount.IS_TICKET_DISCOUNT,
    DISCOUNT_ID: discount.DISCOUNT_ID,
    // TODO line selection ID
    PERCENT_AMOUNT: discount.PERCENT_AMOUNT,
  };

  // flat discount
  if (discount.PERCENT_AMOUNT === null) {
    const remainingDiscount = discount.AMOUNT - discount.AMOUNT_ADJUSTED;

    if (remainingDiscount === 0) {
      // If there is no discount left to apply then don't add it to the selection
      // This is duplicated; we shouldn't ever be in this position but what the heck!

      return {...LINE_SELECTION_DISCOUNT};
    }

    // The whole selection is discounted
    if (
      remainingDiscount >=
      selection.AMOUNT * (discount.IS_TICKET_DISCOUNT ? itemQuantity : 1)
    ) {
      discount.AMOUNT_ADJUSTED +=
        selection.AMOUNT * (discount.IS_TICKET_DISCOUNT ? itemQuantity : 1);

      selection.AMOUNT_DISCOUNT += selection.AMOUNT;
      discountPayload.AMOUNT_ADJUSTED = selection.AMOUNT;

      selection.AMOUNT = 0;
      selection.TOTAL = 0;
    }
    // The whole discount is used up by the selection
    else {
      discount.AMOUNT_ADJUSTED = discount.AMOUNT;

      selection.AMOUNT -= remainingDiscount;
      selection.TOTAL -= remainingDiscount;
      selection.AMOUNT_DISCOUNT += remainingDiscount;

      discountPayload.AMOUNT_ADJUSTED = remainingDiscount;
    }

    discountPayload.AMOUNT = discount.AMOUNT;
  }
  // percent discount
  else {
    // Min in case there is > 100% discount
    const amountToDiscountSelection = Math.min(
      Math.round(selection.AMOUNT * (discount.PERCENT_AMOUNT / 100)),
      selection.AMOUNT
    );

    discount.AMOUNT_ADJUSTED += amountToDiscountSelection * itemQuantity;

    selection.AMOUNT_DISCOUNT += amountToDiscountSelection;
    selection.AMOUNT -= amountToDiscountSelection;
    selection.TOTAL -= amountToDiscountSelection;

    discountPayload.AMOUNT_ADJUSTED = amountToDiscountSelection;
    discountPayload.AMOUNT = amountToDiscountSelection;
  }

  selection.DISCOUNTS.push(discountPayload);
  return discountPayload;
}

/**
 * Given a discount and an item, distribute that discount over the item
 *
 * @param discount
 * @param item
 */
export function applyDiscountToItem(discount, item) {
  const discountPayload = {
    ...LINE_ITEM_DISCOUNT,
    IS_TICKET_DISCOUNT: true,
    DISCOUNT_ID: discount.DISCOUNT_ID,
    PROMOTION_ID: discount.PROMOTION_ID,
    PERCENT_AMOUNT: discount.PERCENT_AMOUNT,
  };

  item.SELECTIONS.sort((a, b) => b.AMOUNT - a.AMOUNT);

  // flat
  if (discount.PERCENT_AMOUNT === null) {
    const remainingDiscount = discount.AMOUNT - discount.AMOUNT_ADJUSTED;

    if (remainingDiscount === 0) {
      // If there is no discount left to apply then don't add it to the selection
      // This is duplicated; we shouldn't ever be in this position but what the heck!
      return;
    }
  }

  let amountApplied = 0;
  for (const selection of item.SELECTIONS) {
    if (selection.AMOUNT === 0) {
      continue;
    }

    if (
      discount.PERCENT_AMOUNT === null &&
      discount.AMOUNT === discount.AMOUNT_ADJUSTED
    ) {
      // This is a flat discount that has been used up
      break;
    }

    const {
      AMOUNT_ADJUSTED: amountAppliedToSelection,
    } = applyDiscountToSelection(discount, selection, item.QUANTITY);
    amountApplied += amountAppliedToSelection;
  }

  discountPayload.AMOUNT_ADJUSTED = amountApplied * (item.QUANTITY || 1);
  discountPayload.AMOUNT =
    discount.PERCENT_AMOUNT === null
      ? discount.AMOUNT
      : amountApplied * (item.QUANTITY || 1);

  item.DISCOUNTS.push(discountPayload);
}

export function calculateDiscounts(items, ticketDiscounts) {
  for (const item of items) {
    calculateProductDiscounts(item);
  }

  propagateDiscounts(items);

  // This shit is tough here (I think we just have this function propagate it while it loops over)
  if (ticketDiscounts && ticketDiscounts.length > 0) {
    calculateTicketDiscounts(items, ticketDiscounts);
  }
}

export function calculateTaxes(items) {
  for (let item of items) {
    for (let selection of item.SELECTIONS) {
      calculateSelectionTax(selection);
    }
  }
}

/**
 * This function assumes that calculateTaxes has already been called. This will take an inclusive tax rate item and recalculate its taxes if a discount has been applied to it
 */
export function recalculateInclusiveTaxes(items) {
  for (let item of items) {
    for (let selection of item.SELECTIONS) {
      if (selection.AMOUNT_DISCOUNT > 0) {
        recalculateInclusiveSelectionTax(selection);
      }
    }
  }
}

export function recalculateInclusiveSelectionTax(selection) {
  // If we are here, then the line item has an inclusive tax rate on it
  // and it has its taxes incorrectly set to be the "without discount" amount
  // this function adjusts those incorrect tax amounts by treating the rates as if they were exclusive.
  const preTaxPrice = selection.AMOUNT;

  selection.AMOUNT_TAX = 0;
  selection.TOTAL = selection.AMOUNT;

  if (selection.TAX_RATES) {
    for (let rate of selection.TAX_RATES) {
      const rateTax = Math.round((rate.AMOUNT_TAX / 100) * preTaxPrice);

      rate.AMOUNT = rateTax;

      selection.AMOUNT_TAX += rateTax;
      selection.TOTAL += rateTax;
    }
  } else {
    selection.AMOUNT_TAX = 0;
  }
}

export function propagateDiscounts(cartPayload) {
  for (const item of cartPayload) {
    item.AMOUNT = 0;
    item.AMOUNT_TAX = 0;
    item.AMOUNT_DISCOUNT = 0;
    item.TOTAL = 0;

    item.AMOUNT =
      item.SELECTIONS.reduce((accum, item) => {
        return accum + item.AMOUNT;
      }, 0) * (item.QUANTITY || 1);

    item.AMOUNT_DISCOUNT =
      item.SELECTIONS.reduce((accum, item) => {
        return accum + (item.AMOUNT_DISCOUNT ?? 0);
      }, 0) * (item.QUANTITY || 1);
  }
}

export function propagateCart(cartPayload) {
  for (let item of cartPayload) {
    item.AMOUNT = 0;
    item.AMOUNT_DISCOUNT = 0;
    item.AMOUNT_TAX = 0;
    item.TOTAL = 0;

    item.TAX_RATES = Object.values(
      item.SELECTIONS.reduce((dict, selection) => {
        if (selection.TAX_RATES) {
          selection.TAX_RATES.forEach((selectionRate) => {
            if (!dict[selectionRate.TAX_ID]) {
              dict[selectionRate.TAX_ID] = {
                ..._deepClone(LINE_ITEM_TAX),

                AMOUNT_TAX: selectionRate.AMOUNT_TAX,
                TAX_ID: selectionRate.TAX_ID,
              };
            }

            if (selectionRate.AMOUNT_ADJUSTED !== null) {
              dict[selectionRate.TAX_ID].AMOUNT_ADJUSTED +=
                selectionRate.AMOUNT_ADJUSTED;
            }

            dict[selectionRate.TAX_ID].AMOUNT += selectionRate.AMOUNT;
          });
        }
        return {...dict};
      }, {})
    );

    item.AMOUNT =
      item.SELECTIONS.reduce((accum, selection) => {
        return accum + selection.AMOUNT;
      }, 0) * (item.QUANTITY || 1);

    item.AMOUNT_TAX =
      item.SELECTIONS.reduce((accum, selection) => {
        return accum + selection.AMOUNT_TAX;
      }, 0) * (item.QUANTITY || 1);

    item.AMOUNT_DISCOUNT =
      item.SELECTIONS.reduce((accum, selection) => {
        return accum + (selection.AMOUNT_DISCOUNT ?? 0);
      }, 0) * (item.QUANTITY || 1);

    item.TOTAL =
      item.SELECTIONS.reduce((accum, selection) => {
        return accum + selection.TOTAL;
      }, 0) * (item.QUANTITY || 1);
  }
}

export function propagateTaxes(cartPayload) {
  for (let item of cartPayload) {
    item.AMOUNT_TAX = 0;
    item.TOTAL = 0;

    item.TAX_RATES = Object.values(
      item.SELECTIONS.reduce((dict, selection) => {
        selection.TAX_RATES.forEach((selectionRate) => {
          if (!dict[selectionRate.TAX_ID]) {
            dict[selectionRate.TAX_ID] = {
              ..._deepClone(LINE_ITEM_TAX),

              AMOUNT_TAX: selectionRate.AMOUNT_TAX,
              TAX_ID: selectionRate.TAX_ID,
            };
          }

          if (selectionRate.AMOUNT_ADJUSTED !== null) {
            dict[selectionRate.TAX_ID].AMOUNT_ADJUSTED +=
              selectionRate.AMOUNT_ADJUSTED;
          }

          dict[selectionRate.TAX_ID].AMOUNT += selectionRate.AMOUNT;
        });

        return {...dict};
      }, {})
    );

    item.AMOUNT_TAX = item.SELECTIONS.reduce((accum, item) => {
      return accum + item.AMOUNT_TAX;
    }, 0);

    item.TOTAL = item.SELECTIONS.reduce((accum, item) => {
      return accum + item.TOTAL;
    }, 0);
  }
}

function handleCartItem(
  cartItem,
  cartPayload,
  cartFees,
  cartGiftCards,
  cartOther,
  removeItem
) {
  if (cartItem.TYPE === "PRODUCT" || cartItem.TYPE === "CUSTOM_PRODUCT") {
    const cartIndex = cartPayload.findIndex((item) => {
      return item.ID === cartItem.ID;
    });

    if (cartIndex === -1) {
      const newItem = _deepClone(cartItem);
      backfillItem(newItem);

      cartPayload.push(newItem);
    } else if (removeItem) {
      cartPayload.splice(cartIndex, 1);
    } else {
      const newItem = _deepClone(cartItem);
      backfillItem(newItem);

      cartPayload.splice(cartIndex, 1, newItem);
    }
  } else if (cartItem.TYPE === "FEE" || cartItem.TYPE === "TICKET_FEE") {
    const cartIndex = cartFees.findIndex((item) => {
      return item.ID === cartItem.ID;
    });

    if (cartIndex === -1) {
      cartFees.push(cartItem);
    } else if (removeItem) {
      cartFees.splice(cartIndex, 1);
    } else {
      cartFees.splice(cartIndex, 1, cartItem);
    }
  } else if (cartItem.TYPE === "GIFT_CARD") {
    const cartIndex = cartGiftCards.findIndex((item) => {
      return item.ID === cartItem.ID;
    });

    if (cartIndex === -1) {
      cartGiftCards.push(cartItem);
    } else if (removeItem) {
      cartGiftCards.splice(cartIndex, 1);
    } else {
      cartGiftCards.splice(cartIndex, 1, cartItem);
    }
  } else {
    const cartIndex = cartOther.findIndex((item) => {
      return item.ID === cartItem.ID;
    });

    if (cartIndex === -1) {
      cartOther.push(cartItem);
    } else if (removeItem) {
      cartOther.splice(cartIndex, 1);
    } else {
      cartOther.splice(cartIndex, 1, cartItem);
    }
  }
}

export function fetchGiftCardLineItem({
  price,
  selections = [],
  discounts = [],
  quantity = 1,
  notes = "",
  logo = null,
  ...extra
}) {
  const giftCardPayload = {
    ..._deepClone(LINE_ITEM),
    ID: Math.round(Math.random() * 1000000),
    QUANTITY: quantity,
    SELECTIONS: selections,
    NOTES: notes,
  };

  giftCardPayload.NAME = "Gift Card";
  giftCardPayload.TYPE = "GIFT_CARD";

  if (!hasVariantPricing(giftCardPayload)) {
    addAutomaticVariantSelection(giftCardPayload, price, []);
  }

  giftCardPayload.DISCOUNTS = discounts;
  giftCardPayload.OBJECT_ID = null;
  giftCardPayload.LOGO = logo || "appicon.png";
  giftCardPayload.AMOUNT = price;
  giftCardPayload.TOTAL = price;

  return {...giftCardPayload, ...extra};
}

export function fetchLineItem({
  product,
  discounts = [],
  rates = [], // these still have to get passed in since we may have no selections or need to add an automatic variant
  selections,
  quantity,
  notes,
  excludeProduct = false,
  ...extra
}) {
  // TODO this needs to re fetch each of the selections so they are reset
  const itemPayload = {
    ..._deepClone(LINE_ITEM),
    ID: Math.round(Math.random() * 1000000),
    QUANTITY: quantity,
    SELECTIONS: selections,
    NOTES: notes,
  };

  if (product) {
    itemPayload.NAME = product.NAME;
    itemPayload.TYPE = "PRODUCT";

    if (!hasVariantPricing(itemPayload)) {
      addAutomaticVariantSelection(itemPayload, product.PRICE, rates);
    }

    itemPayload.TAX_RATES = []; // we don't set the rates here since they live in the selections, this gets populated when added to cartl
    itemPayload.DISCOUNTS = discounts;
    itemPayload.OBJECT_ID = product.ID;
    itemPayload.LOGO = product.LOGO_LOW || product.LOGO;

    if (!excludeProduct) {
      itemPayload.product = {..._deepClone(product)};
    }
  }

  return {...itemPayload, ...extra};
}

export function fetchLineItemSelection({
  selections = [],
  modifier = null,
  option = null,
  quantity,
  rates = [],
  ...extra
}) {
  const selectionPayload = _deepClone(LINE_ITEM_SELECTION);

  const isVariant = modifier?.TYPE === 5;
  const variantSelection = selections.find((item) => {
    return item.MODIFIER_TYPE === 5;
  });

  if (quantity) {
    selectionPayload.QUANTITY = quantity;
  }

  if (modifier) {
    selectionPayload.MODIFIER_ID = modifier.ID;
    selectionPayload.MODIFIER_NAME = modifier.NAME;
    selectionPayload.MODIFIER_TYPE = modifier.TYPE;
    selectionPayload.SEQ = modifier.SEQ;
  }

  if (option) {
    selectionPayload.OPTION_ID = option.ID;
    selectionPayload.OPTION_NAME = option.NAME;
    selectionPayload.MODIFIER = option.MODIFIER;

    selectionPayload.AMOUNT_TAX = 0;

    let variantModifier = 1;
    if (
      !isVariant &&
      variantSelection &&
      MULTIPLIER_MODIFIERS.includes(modifier.TYPE)
    ) {
      variantModifier = variantSelection.MODIFIER || 1;
    }

    selectionPayload.AMOUNT = Math.round(option.PRICE * variantModifier);
    selectionPayload.TOTAL = Math.round(option.PRICE * variantModifier);

    if (selectionPayload.QUANTITY > 1) {
      selectionPayload.AMOUNT *= selectionPayload.QUANTITY;
      selectionPayload.TOTAL *= selectionPayload.QUANTITY;
    }
  }

  for (let rate of rates) {
    const ratePayload = {
      ...LINE_SELECTION_TAX,
      AMOUNT_TAX: rate.RATE,
      TAX_ID: rate.ID,
    };

    if (rate.IS_INCLUSIVE) {
      ratePayload.AMOUNT_ADJUSTED = 0;
    }

    selectionPayload.TAX_RATES.push(ratePayload);
  }

  // DISCOUNTS get stored in the line item then distributed over the selections in update cart
  // so at this point the selection.DISCOUNTS = []

  return {...selectionPayload, ...extra};
}

export function addAutomaticVariantSelection(item, price, rates) {
  const automaticSelection = fetchLineItemSelection({
    rates,
    modifier: {
      TYPE: CUSTOMIZATION_TYPES.AUTOMATIC_VARIANT,
      NAME: "Price",
      ID: null,
      SEQ: 127,
    },
    option: {ID: null, NAME: "Price", PRICE: price},
    quantity: 1,
  });

  if (!item.SELECTIONS) {
    item.SELECTIONS = [];
  }

  item.SELECTIONS.push(automaticSelection);
}

export function calculateSelectionTax(selection) {
  const preTaxPrice = selection.AMOUNT;

  if (selection.TAX_RATES) {
    for (let rate of selection.TAX_RATES) {
      if (rate.AMOUNT_ADJUSTED !== null && rate.AMOUNT_ADJUSTED !== undefined) {
        // Inclusive tax rate case
        let postTaxPrice = preTaxPrice; // just renaming the misnomer

        const percent = _decimalPercent(rate.AMOUNT_TAX);

        const preTaxAmount = Math.round(postTaxPrice / percent);
        const rateTaxAmount = postTaxPrice - preTaxAmount;

        rate.AMOUNT_ADJUSTED = rateTaxAmount;
        rate.AMOUNT = rateTaxAmount;

        selection.AMOUNT -= rateTaxAmount;
        selection.AMOUNT_TAX += rateTaxAmount;
      } else {
        // exclusive tax rate case
        const rateTax = Math.round((rate.AMOUNT_TAX / 100) * preTaxPrice);

        rate.AMOUNT = rateTax;

        selection.AMOUNT_TAX += rateTax;
        selection.TOTAL += rateTax;
      }
    }
  } else {
    selection.AMOUNT_TAX = 0;
  }
}

export function fetchLineItemDiscount({discount = null, promotion = null}) {
  const lineItemDiscount = _deepClone({...LINE_ITEM_DISCOUNT});

  if (discount) {
    lineItemDiscount.DISCOUNT_ID = discount.ID;

    if (discount.TYPE === DISCOUNT_TYPES.PERCENT) {
      lineItemDiscount.PERCENT_AMOUNT = discount.CONTENT;
      lineItemDiscount.AMOUNT = discount.CONTENT;
    } else if (discount.TYPE === DISCOUNT_TYPES.FLAT) {
      lineItemDiscount.AMOUNT = discount.CONTENT;
      lineItemDiscount.PERCENT_AMOUNT = null;
    }
  } else if (promotion) {
    lineItemDiscount.PROMOTION_ID = promotion.ID;
    let {FLAT_FEE, PERCENTAGE} = promotion.DISCOUNT;

    if (FLAT_FEE) {
      lineItemDiscount.AMOUNT = FLAT_FEE;
      lineItemDiscount.PERCENT_AMOUNT = null;
    } else if (PERCENTAGE) {
      lineItemDiscount.PERCENT_AMOUNT = PERCENTAGE;
      lineItemDiscount.AMOUNT = PERCENTAGE;
    }
  }
  return lineItemDiscount;
}

export function fetchTicketDiscount({discount = null, promotion = null}) {
  const ticketDiscount = _deepClone({...TICKET_DISCOUNT});

  if (discount) {
    ticketDiscount.DISCOUNT_ID = discount.ID;

    if (discount.TYPE === TICKET_DISCOUNT_TYPES.PERCENT) {
      ticketDiscount.PERCENT_AMOUNT = discount.CONTENT;
      ticketDiscount.AMOUNT = discount.CONTENT;
    } else if (discount.TYPE === TICKET_DISCOUNT_TYPES.FLAT) {
      ticketDiscount.AMOUNT = discount.CONTENT;
      ticketDiscount.PERCENT_AMOUNT = null;
    }
  } else if (promotion) {
    ticketDiscount.PROMOTION_ID = promotion.ID;
    let {FLAT_FEE, PERCENTAGE} = promotion.DISCOUNT;

    if (FLAT_FEE) {
      ticketDiscount.AMOUNT = FLAT_FEE;
      ticketDiscount.PERCENT_AMOUNT = null;
    } else if (PERCENTAGE) {
      ticketDiscount.PERCENT_AMOUNT = PERCENTAGE;
      ticketDiscount.AMOUNT = PERCENTAGE;
    }
  }

  return ticketDiscount;
}

export function fetchLineTaxRates({rates, selections}) {
  const taxPayload = _deepClone(LINE_SELECTION_TAX);
}

function _deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function _decimalPercent(percent) {
  return percent / 100 + 1;
}

/**
 * Compare function for two discounts
 *
 * @param a
 * @param b
 */
function discountCmp(a, b) {
  // Sort high to low flat discounts then high to low percent discounts
  if (a.PERCENT_AMOUNT === null) {
    // b is flat
    if (b.PERCENT_AMOUNT === null) {
      return b.AMOUNT - a.AMOUNT;
    }
    // b is pct
    else {
      return -1;
    }
  }
  // a is pct
  else {
    // b is flat
    if (b.PERCENT_AMOUNT === null) {
      return 1;
    }
    // b is pct
    else {
      return b.PERCENT_AMOUNT - a.PERCENT_AMOUNT;
    }
  }
}

export function hasVariantPricing(item) {
  return (
    item.SELECTIONS &&
    item.SELECTIONS.findIndex(
      (selection) =>
        selection.MODIFIER_TYPE === CUSTOMIZATION_TYPES.VARIANT ||
        selection.MODIFIER_TYPE === CUSTOMIZATION_TYPES.AUTOMATIC_VARIANT
    ) !== -1
  );
}

export function hasInclusiveTaxRate(item) {
  if (!item.SELECTIONS || item.SELECTIONS.length === 0) {
    return false;
  }

  return item.SELECTIONS.some(
    (_selection) =>
      _selection.TAX_RATES &&
      _selection.TAX_RATES.length > 0 &&
      _selection.TAX_RATES.some((_tr) => {
        return (
          _tr.AMOUNT_ADJUSTED !== null && _tr.AMOUNT_ADJUSTED !== undefined
        );
      })
  );
}
